import { useEffect, useRef } from "react";
import Task from "../../../../model/Task";

interface TaskOpenShakeProps {
    item?: Task;
    commboxId: number | null;
    enable: boolean;
}

const SCREEN_SHAKE_CLASS = 'screen-shake-67';
const SCREEN_SHAKE_DURATION_MS = 2800;
const SCREEN_SHAKE_TRIGGER_DELAY_MS = 5000;

function getScreenShakeTarget() {
    if (globalThis.document === undefined) {
        return null;
    }
    return globalThis.document.getElementById('root') || globalThis.document.body;
}

function hasCommentWith67(task?: Task) {
    return !!task?.comments?.some((comment) => (comment.comment || '').includes('67'));
}

function runImperativeScreenShake(target: HTMLElement) {
    if (typeof target.animate !== 'function') {
        return;
    }

    const SNAP = 'cubic-bezier(.8, 0, .2, 1)';
    const SETTLE = 'cubic-bezier(.55, 0, .45, 1)';

    target.animate([
        { offset: 0,    transform: 'translate3d(0, 0, 0) rotate(0deg) scale(1)',              easing: 'ease-out' },
        { offset: 0.06, transform: 'translate3d(-14px, 0, 0) rotate(-0.4deg) scale(1.0022)',  easing: SNAP },
        { offset: 0.17, transform: 'translate3d(11.5px, 0, 0) rotate(0.32deg) scale(1.0012)', easing: SNAP },
        { offset: 0.28, transform: 'translate3d(-9px, 0, 0) rotate(-0.26deg)',                easing: SNAP },
        { offset: 0.38, transform: 'translate3d(7px, 0, 0) rotate(0.2deg)',                   easing: SNAP },
        { offset: 0.48, transform: 'translate3d(-5.5px, 0, 0) rotate(-0.16deg)',              easing: SNAP },
        { offset: 0.57, transform: 'translate3d(4.3px, 0, 0) rotate(0.12deg)',                easing: SETTLE },
        { offset: 0.66, transform: 'translate3d(-3.3px, 0, 0) rotate(-0.09deg)',              easing: SETTLE },
        { offset: 0.74, transform: 'translate3d(2.4px, 0, 0) rotate(0.07deg)',                easing: SETTLE },
        { offset: 0.81, transform: 'translate3d(-1.7px, 0, 0) rotate(-0.05deg)',              easing: SETTLE },
        { offset: 0.87, transform: 'translate3d(1.1px, 0, 0) rotate(0.03deg)',                easing: SETTLE },
        { offset: 0.92, transform: 'translate3d(-0.7px, 0, 0) rotate(-0.02deg)',              easing: SETTLE },
        { offset: 0.95, transform: 'translate3d(0.4px, 0, 0) rotate(0.01deg)',                easing: 'ease-out' },
        { offset: 0.98, transform: 'translate3d(-0.2px, 0, 0) rotate(0deg)',                  easing: 'ease-out' },
        { offset: 1,    transform: 'translate3d(0, 0, 0) rotate(0deg) scale(1)' },
    ], {
        duration: SCREEN_SHAKE_DURATION_MS,
        easing: 'linear',
        iterations: 1,
    });
}

export default function TaskOpenShake({ item, commboxId, enable }: TaskOpenShakeProps) {
    const lastShakenTaskIdRef = useRef<number | null>(null);
    const shakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    function clearScreenShakeTimeout() {
        if (shakeTimeoutRef.current !== null) {
            globalThis.clearTimeout(shakeTimeoutRef.current);
            shakeTimeoutRef.current = null;
        }
    }

    function triggerScreenShake() {
        const target = getScreenShakeTarget();
        if (!target) {
            return;
        }

        clearScreenShakeTimeout();
        target.classList.remove(SCREEN_SHAKE_CLASS);

        const addAnimationClass = () => {
            target.classList.add(SCREEN_SHAKE_CLASS);

            const animationName = globalThis.getComputedStyle(target).animationName;
            if (!animationName || animationName === 'none') {
                runImperativeScreenShake(target);
            }

            shakeTimeoutRef.current = globalThis.setTimeout(() => {
                target.classList.remove(SCREEN_SHAKE_CLASS);
                shakeTimeoutRef.current = null;
            }, SCREEN_SHAKE_DURATION_MS);
        };

        if (typeof globalThis.requestAnimationFrame === 'function') {
            globalThis.requestAnimationFrame(addAnimationClass);
            return;
        }

        addAnimationClass();
    }

    useEffect(() => {
        if (!item?.id || !commboxId || !enable) {
            return;
        }
        if (lastShakenTaskIdRef.current === item.id) {
            return;
        }
        if (!hasCommentWith67(item)) {
            return;
        }

        lastShakenTaskIdRef.current = item.id;
        const triggerTimeout = globalThis.setTimeout(() => {
            triggerScreenShake();
        }, SCREEN_SHAKE_TRIGGER_DELAY_MS);

        return () => {
            globalThis.clearTimeout(triggerTimeout);
        }
    }, [item?.id, item?.comments, commboxId, enable]);

    useEffect(() => {
        return () => {
            clearScreenShakeTimeout();
            const target = getScreenShakeTarget();
            if(target) target?.classList.remove(SCREEN_SHAKE_CLASS);
        }
    }, []);

    return null;
}
